import { newMarkPosition } from './GeoUtils';
/**
 * Calculate triangle course mark positions.
 *
 * `totalDistance` is the TOTAL sailed distance for the full course (all beats).
 *
 * Path decomposition (mark 4 is on the wind axis between startMP and mark 1):
 *   S→4(ld) + 4→1(x) + [1→2(t) + 2→4(t) + 4→1(x)]×(N-1) + 1→4(x) + 4→F(ld)
 *   Total = 2·ld + x·(N+1) + x·(N-1)/cos(angleTo2)
 *   Solving: x = (totalDistance - 2·ld) / (N+1 + (N-1)/cos(angleTo2))
 *
 * Reach legs are symmetric: 1→2 = 2→4 = t = x / (2·cos(angleTo2))
 * This makes both reach legs hit the same angle relative to the downwind axis.
 *
 * @param {number} startLat - Start midpoint latitude
 * @param {number} startLng - Start midpoint longitude
 * @param {number} windDir - Wind direction (degrees, toward windward)
 * @param {number} totalDistance - Total course length (meters)
 * @param {number} angleTo2 - Reach angle (degrees): offset from downwind toward port
 * @param {number} leewardDistance - Distance from startMP to mark 4 upwind (meters)
 * @param {number} numBeats - Number of windward beats (1–4)
 * @returns {Array} [mark1, mark2, mark4] position objects
 */
export function calculateTriangleCourseMarks(startLat, startLng, windDir, totalDistance, angleTo2, leewardDistance, numBeats) {
  const ld = leewardDistance;
  const N = Math.max(1, numBeats || 1);
  const cosA = Math.cos(angleTo2 * Math.PI / 180);

  if (cosA <= 0) {
    console.error('calculateTriangleCourseMarks: angleTo2 must be < 90°');
  }

  // Windward leg above mark 4 (x): derived from total distance formula
  // For N=1 the (N-1)/cosA term is 0, so x = (total-2ld)/2 — correct for straight out-and-back.
  const x = (totalDistance - 2 * ld) / ((N + 1) + (N - 1) / cosA);

  // Mark 4: on axis, leewardDistance upwind of startMP
  const mark4 = newMarkPosition(startLat, startLng, windDir, ld);

  // Mark 1: on axis, (ld + x) upwind of startMP
  const mark1 = newMarkPosition(startLat, startLng, windDir, ld + x);

  // Mark 2: from mark 1, reach leg t at bearing (windDir+180+angleTo2).
  // t = x/(2·cosA) ensures 1→2 = 2→4 and both legs hit angleTo2 from the downwind axis.
  const reachLeg = x / (2 * cosA);
  const bearingTo2 = (windDir + 180 + angleTo2) % 360;
  const mark2 = newMarkPosition(mark1.lat, mark1.lng, bearingTo2, reachLeg);

  return [mark1, mark2, mark4];
}
